import * as React from 'react';
import { Modal, ModalHeader, ModalBody, Row, Col, Card, CardBody, Badge } from 'reactstrap';
import { FaQuestionCircle, FaRobot, FaDatabase, FaFileAlt, FaCode, FaInfoCircle, FaGlobe, FaLayerGroup, FaMicrochip, FaTags, FaHistory } from 'react-icons/fa';

interface AIDesignerGuideModalProps {
    isOpen: boolean;
    toggle: () => void;
}

export const AIDesignerGuideModal: React.FC<AIDesignerGuideModalProps> = ({ isOpen, toggle }) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className="border-0">
            <ModalHeader toggle={toggle} className="bg-dark text-white">
                <FaQuestionCircle className="me-2 text-info" /> AI Designer Framework Guide
            </ModalHeader>
            <ModalBody className="p-4" style={{ backgroundColor: '#f8f9fa', maxHeight: '70vh', overflowY: 'auto' }}>    
                
                <h5 className="fw-bold text-primary mb-1">1. The Global vs. Sequential Architecture</h5>
                <p className="small text-muted mb-3">To eliminate model "skimming" and maximize evaluation accuracy, this framework decouples wide environmental constraints from highly targeted execution tasks.</p>

                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <Card className="h-100 border-start border-4 border-dark shadow-sm border-0">
                            <CardBody>
                                <h6 className="fw-bold text-dark"><FaGlobe className="text-info me-2"/>Global Environment Layer</h6>
                                <p className="text-xs text-muted mb-0">
                                    Contains parameters applied to <strong>every single step</strong> in the execution sequence automatically. Use this zone for your high-level System Identity Persona and comprehensive reference source files.
                                </p>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="h-100 border-start border-4 border-primary shadow-sm border-0">
                            <CardBody>
                                <h6 className="fw-bold text-primary"><FaLayerGroup className="me-2"/>Sequential Graph Steps</h6>
                                <p className="text-xs text-muted mb-0">
                                    Isolated processing cells executed one after the other. Each step runs independently using its own scoped instructions and target document constraints to preserve the AI's attention span.
                                </p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <div className="p-3 bg-white border-start border-4 border-info shadow-sm mb-4">
                    <h6 className="fw-bold mb-1 text-dark"><FaMicrochip className="me-2 text-info" /> Scoping Brains and Engine Knobs</h6>
                    <p className="small mb-0 text-muted">
                        Inside each processing phase, you can switch the <strong>Execution Brain</strong> to balance costs against performance metrics—including custom, highly specialized fine-tuned extraction models.
                    </p>
                </div>

                <hr />

                <h5 className="fw-bold text-primary mb-3">2. Understanding Input Slots</h5>
                <p className="small text-muted">Each step or global block utilizes target slots to inject data dynamically or state fixed boundaries into the execution matrix.</p>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <Card className="h-100 shadow-sm border-0">
                            <CardBody>
                                <h6 className="fw-bold"><FaDatabase className="text-warning me-2"/>Data Objects</h6>
                                <p className="text-xs mb-0">Injects dynamic relational records or structured field elements at runtime. Use this as your primary transaction target.</p>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="h-100 shadow-sm border-0">
                            <CardBody>
                                <h6 className="fw-bold"><FaFileAlt className="text-info me-2"/>File Bundles</h6>
                                <p className="text-xs mb-0">Uploads unstructured binary text documents into the sandbox simulator or maps file streams loaded by integrated platform modules.</p>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="h-100 shadow-sm border-0">
                            <CardBody>
                                <h6 className="fw-bold"><FaCode className="text-success me-2"/>Static Instructions</h6>
                                <p className="text-xs mb-0">The strict analytical logic, processing rules, checklist items, and structural format schemas enforced within a given execution turn.</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <hr />

                <h5 className="fw-bold text-primary mb-1">3. Pro-Tip: Context Aliases and Scoping Labels</h5>
                <p className="small text-muted mb-2">When utilizing file or data slots across global environment wrappers or isolated sequential steps, assigning pristine naming tokens is vital for context separation.</p>
                
                <div className="p-3 bg-white rounded-3 border-start border-4 border-success shadow-sm mb-4">
                    <h6 className="fw-bold mb-1 text-dark"><FaTags className="me-2 text-success" /> Exact References Prevent Cross-Contamination</h6>
                    <p className="small mb-0 text-muted">
                        Always assign clear, strict filename tokens or labels (e.g., <code>#BaselineStandard</code> globally or <code>#TargetEvidence</code> contextually inside a phase block). 
                        The backend orchestrator explicitly tells the model: <em>"The following asset context represents [Alias Name]."</em> 
                        This strict token pinning prevents the AI engine from confusing global reference rules with local phase inputs, ensuring the model isolates its retrieval fields perfectly.
                    </p>
                </div>

                <hr />

                {/* NEW COMPONENT SECTION: Orchestrating the Final Pass Ledger */}
                <h5 className="fw-bold text-primary mb-1">4. Utilizing the Final Pass Ledger</h5>
                <p className="small text-muted mb-2">As the sequential graph advances, the engine automatically accumulates all text outputs from preceding phases into a running ledger.</p>

                <div className="p-3 bg-white rounded-3 border-start border-4 border-primary shadow-sm mb-4">
                    <h6 className="fw-bold mb-2 text-dark"><FaHistory className="me-2 text-primary" /> Referencing Prior Phase Output Records</h6>
                    <p className="small text-muted mb-3">
                        To run a concluding synthesis pass (for cleaning formatting, summarizing findings, or applying secondary conditional logic), add one final <strong>Sequential Phase</strong> at the bottom of your step configuration sequence.
                    </p>
                    <p className="small text-muted mb-2">
                        Inside the <strong>Static Instruction</strong> block of your concluding phase, you can strictly target the compiled memory track by instructing the AI engine to evaluate the <code>PRECEDING PHASES OUTPUT LEDGER</code> block.
                    </p>
                    <div className="bg-light p-2 rounded border font-monospace text-xs text-dark mb-0" style={{ fontSize: '11px' }}>
                        <strong>Example Final Step Prompt:</strong><br />
                        "Review the accumulated findings contained inside the <strong>PRECEDING PHASES OUTPUT LEDGER</strong> block. Cross-examine the identified discrepancies against the global standards, eliminate duplicate items, and return the filtered compilation strictly structured against our requested UI presentation JSON schema format."
                    </div>
                </div>

                <hr />

                <h5 className="fw-bold text-primary mb-3">5. The "Meta-Prompt" (For ChatGPT/Claude)</h5>
                <div className="bg-dark p-3 rounded-3 position-relative mb-4">
                    <Badge color="info" className="position-absolute top-0 end-0 m-2">Copy this to your AI</Badge>
                    <pre className="text-info small mb-0 font-monospace" style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                        {`I am designing an AI processing tool in a multi-phase sequential sandbox. Help me write the prompt.
The tool utilizes a sequential pipeline graph of context layers. I need you to draft:

1. SYSTEM PERSONA: (Define a high-level expert operational or analytical persona)
2. STATIC INSTRUCTION: (Define how to analyze the target entity against our global reference structures)
3. JSON SCHEMA: (I need the output returned in a strict JSON format with fields: 
"summary", "critical_exceptions", "secondary_exceptions", "execution_status")

CONTEXT: I will be providing a structured Data Object and a document attachment representing the source file. 
The goal is to identify processing variances and operational discrepancies.`}
                    </pre>
                </div>
                
                <hr />

                <h5 className="fw-bold text-primary mb-3">6. Target Data Schema (JSON Example)</h5>
                <p className="small text-muted">Use these object paths when instructing your prompts to reference specific system variables.</p>
                <div className="bg-dark p-3 rounded-3 mb-4" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    <pre className="text-success small mb-0 font-monospace" style={{ fontSize: '11px' }}>
{`{
  "Header": {
    "FileNumber": "string",
    "RecordIdentifier": "string",
    "SystemEntity": "string",
    "ScopeDescription": "string"
  },
  "ConfigurationInfo": {
    "PolicyNumber": "string",
    "ThresholdLimit": "double",
    "Parameters": [
      {
        "Type": "string",
        "Value": "string",
        "VarianceReason": "string"
      }
    ]
  },
  "Financials": {
    "ItemizedBreakdown": {
      "Category_A": "double",
      "Category_B": "double",
      "Category_C": "double"
    },
    "TotalAggregatedValue": "double",
    "TransactionCount": "int",
    "IsLocked": "boolean"
  },
  "RecentActivity": [
    {
      "CreatedDate": "datetime",
      "Author": "string",
      "Text": "string"
    }
  ]
}`}
                    </pre>
                </div>

                <div className="alert alert-warning border-0 shadow-sm d-flex align-items-start mb-0">
                    <FaInfoCircle className="me-3 mt-1 flex-shrink-0" size={20} style={{ marginRight: '10px' }} />
                    <div>
                        <h6 className="fw-bold mb-1">Prompting Tip</h6>
                        <p className="small mb-0">
                            Tell your LLM: <em>"Cross-reference <strong>Financials.ItemizedBreakdown.Category_A</strong> against the assigned step <strong>File Context Alias</strong> to verify that all transactional values completely adhere to the global reference constraints."</em>
                        </p>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default AIDesignerGuideModal;